import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  ChevronDown,
  Package,
  ShoppingCart,
  Truck,
  AlertTriangle,
  FileText,
  BarChart3,
  Settings,
  BookOpen,
  Beaker,
  LayoutGrid,
  Package2,
  Droplet,
  Printer,
  Film,
  Flame,
  Scan,
  Archive,
  Boxes,
  FolderArchive,
  PackageOpen,
  ClipboardCheck,
  ClipboardList,
  Tag,
  Ship,
  Bell,
  Braces,
  DollarSign,
  SlidersHorizontal,
  Activity,
  ArrowDownUp,
  ArrowLeftRight,
  Layers,
  Inbox,
  Plug,
  RefreshCw,
  Rocket,
  Cable,
  ShieldMinus,
  MessagesSquare,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { BuildIdLabel } from '@/components/BuildIdLabel';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/hooks/use-role';
import { useBranding } from '@/hooks/useBranding';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { useBatchNotification } from '@/contexts/BatchNotificationContext';
import { visibleWorkspaces } from '@/lib/page-registry';
import { useStationSettings } from '@/features/production-settings/hooks/useStationSettings';
import { useProductionSettings } from '@/features/production-settings/hooks/useProductionSettings';
import { useIssueCounts } from '@/features/production-issues';
import { computeVisibleIssueTotal, CX_HIDDEN_ISSUE_TABS, CLIENT_HIDDEN_ISSUE_TABS } from '@/features/production-issues/issueTabs';
import { usePortlessBlockedCount } from '@/features/production-issues/hooks/usePortlessBlocked';
import { useShippingFailures } from '@/features/packing-station/hooks/useShipping';
import { useDashboard } from '@/features/production-orders/hooks/useDashboard';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { selectSetupProgress } from '@/features/onboarding/lib/steps';
import { shouldShowSetupEntry } from '@/features/onboarding/lib/setup-mode';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { buildStationNavItems, type NavItem } from './station-nav-items';

/**
 * The single `/production/*` path a pure client may reach: the issues on its own
 * orders at the 3PL's floor. The Production workspace itself stays hidden for
 * them — this is one page, not an entry into the floor.
 */
const CLIENT_PRODUCTION_PATH = '/production/issues';


/** A labelled (or, when `label` is omitted, unlabelled top) cluster of nav items. */
interface NavSection {
  id: string;
  label?: string;
  items: NavItem[];
  /** When set, the section's label becomes a collapsible accordion trigger
   *  (chevron affordance). The value is the DEFAULT state on first visit;
   *  a persisted user choice (localStorage) wins thereafter, and a section
   *  containing the active route auto-expands. Omit for a fixed label. */
  collapsible?: 'closed' | 'open';
}

const STATION_LABELS: Record<string, string> = {
  Picker: 'Picker Station',
  Printer: 'Printer Station',
  Catcher: 'Catcher Station',
  Packer: 'Packer Station',
  CX: 'CX View',
  'DTF Printer': 'DTF Printer Station',
};

// Shared nav-item chrome — extracted so every menu item (main, section, footer,
// partner) renders identically. Active-state colouring lives here, once.
const NAV_ITEM_CLASS = 'text-base h-9';
const NAV_ITEM_ACTIVE_CLASS =
  'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground';
// Quiet uppercase group label — the existing SidebarGroupLabel colour, plus the
// design-system group-label casing (DESIGN §13.4).
const GROUP_LABEL_CLASS = 'text-sidebar-foreground/50 uppercase tracking-wide';

/** One sidebar row. Renders icon, optional ping indicator, label, optional badge. */
function SidebarNavItem({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip={item.label}
        className={cn(NAV_ITEM_CLASS, active && NAV_ITEM_ACTIVE_CLASS)}
      >
        <Link to={item.path}>
          <div className="relative">
            <Icon className="h-4 w-4" />
            {item.indicator && (
              <span className="absolute -top-1 -right-1 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
            )}
          </div>
          {item.progress ? (
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate">{item.label}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-sidebar-foreground/60">
                  {item.progress.done}/{item.progress.total}
                </span>
              </span>
              <span className="h-1 w-full overflow-hidden rounded-full bg-sidebar-foreground/10">
                <span
                  className="block h-full rounded-full bg-threadheads-purple transition-[width] duration-500 motion-reduce:transition-none"
                  style={{ width: `${Math.round((item.progress.done / item.progress.total) * 100)}%` }}
                />
              </span>
            </span>
          ) : (
            <span>{item.label}</span>
          )}
          {item.badge && item.badge > 0 && (
            <Badge
              variant={item.id === 'prod-issues' ? 'destructive' : 'secondary'}
              className="ml-auto h-5 min-w-[20px] px-1.5 text-xs"
            >
              {item.badge}
            </Badge>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * One nav section (inventory or production workspace). Fixed-label sections
 * render exactly as before. Sections carrying `collapsible` render their label
 * as an accordion trigger (chevron affordance) with:
 *  - default state from `section.collapsible` ('closed' | 'open') on first visit;
 *  - the user's own choice persisted in localStorage (wins over the default);
 *  - auto-expand when the active route lives inside the section, so the active
 *    item is never hidden (on mount, and whenever navigation enters the section);
 *  - forced-open while the whole sidebar is collapsed to icons — the label
 *    (and thus the accordion affordance) is hidden in icon mode, so the items
 *    must stay reachable as icons.
 * The expand/collapse motion is the canonical site-wide grid transition from
 * index.css ([data-radix-collapsible-content], prefers-reduced-motion built in).
 *
 * CONSTRAINT: a closed section renders its children to zero pixels, so any
 * `badge` or `indicator` on an item inside it is invisible until the user
 * opens the section. Do NOT put a badged/indicatored item in a `collapsible`
 * section unless you have solved roll-up (surfacing the signal on the section
 * label) — and note that roll-up is only worth building for a signal that
 * means "work is waiting". A signal that merely confirms an action the user
 * just took is already carried by its toast; rolling that up onto a collapsed
 * label adds noise, not information. The two collapsible sections today
 * (`stock-ops`, `prod-stations`) carry no badges. `prod-stations` holds the
 * one `indicator` — the picker's new-batch dot — and it is deliberately left
 * to render only when the section is open, because `OrdersPage` toasts the
 * same event on the same line.
 */
function NavSectionGroup({
  section,
  visibleItems,
  isActive,
}: {
  section: NavSection;
  visibleItems: NavItem[];
  isActive: (item: NavItem) => boolean;
}) {
  const { state: sidebarState, isMobile } = useSidebar();
  const isCollapsible = section.collapsible !== undefined;
  const hasActiveItem = visibleItems.some((item) => isActive(item));
  const storageKey = `sidebar.section.${section.id}`;

  const [open, setOpen] = useState<boolean>(() => {
    if (!isCollapsible) return true;
    if (hasActiveItem) return true;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === 'open') return true;
      if (stored === 'closed') return false;
    } catch {
      /* localStorage unavailable — fall through to the seeded default */
    }
    return section.collapsible === 'open';
  });

  // Auto-expand when navigation moves the active route into this section.
  // Depends on hasActiveItem (not location) so it only fires on the
  // outside->inside transition, leaving a manual collapse intact otherwise.
  useEffect(() => {
    if (isCollapsible && hasActiveItem) setOpen(true);
  }, [isCollapsible, hasActiveItem]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      try {
        window.localStorage.setItem(storageKey, next ? 'open' : 'closed');
      } catch {
        /* ignore persistence failures */
      }
    },
    [storageKey],
  );

  if (!isCollapsible) {
    return (
      <SidebarGroup>
        {section.label && (
          <SidebarGroupLabel className={GROUP_LABEL_CLASS}>{section.label}</SidebarGroupLabel>
        )}
        <SidebarGroupContent>
          <SidebarMenu>
            {visibleItems.map((item) => (
              <SidebarNavItem key={item.id} item={item} active={isActive(item)} />
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  // Icon-collapsed sidebar hides the label/trigger — keep items reachable.
  // `sidebarState` is derived from the DESKTOP open flag only (sidebar.tsx:115,
  // `open ? 'expanded' : 'collapsed'`) and is NOT mobile-aware, while <Sidebar>
  // renders a full-width Sheet with fully visible labels whenever `isMobile`.
  // So on mobile the two disagree: state reads 'collapsed' while the trigger is
  // plainly on screen. Without `&& !isMobile` every section would force open and
  // the chevron would contradict it. Mobile has a real trigger, so honour `open`.
  const effectiveOpen = sidebarState === 'collapsed' && !isMobile ? true : open;

  return (
    <Collapsible open={effectiveOpen} onOpenChange={handleOpenChange} asChild>
      <SidebarGroup>
        <CollapsibleTrigger asChild>
          <SidebarGroupLabel
            asChild
            className={cn(
              GROUP_LABEL_CLASS,
              'cursor-pointer select-none justify-between gap-2 hover:text-sidebar-foreground/80',
            )}
          >
            <button type="button">
              <span className="truncate">{section.label}</span>
              {/* Chevron tracks effectiveOpen, not `open` — it must point at
                  what the content is actually doing, including when
                  effectiveOpen overrides `open`. */}
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 shrink-0 transition-transform duration-200 motion-reduce:transition-none',
                  !effectiveOpen && '-rotate-90',
                )}
                aria-hidden="true"
              />
            </button>
          </SidebarGroupLabel>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => (
                <SidebarNavItem key={item.id} item={item} active={isActive(item)} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

interface AppSidebarProps {
  /**
   * BLU-1376 (D7): true when the org just finished setup in this session, so
   * the sidebar fades in as it appears (the existing 220 ms fade, none under
   * reduced motion). AppLayout decides; see its sticky setup-org ref.
   */
  reveal?: boolean;
}

export function AppSidebar({ reveal = false }: AppSidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspace, setWorkspace } = useWorkspace();
  const { organization } = useOrganization();
  // Set<string>, not Set<HideableNavSection>: NavSection.id is a plain string
  // and this is only a lookup. The narrow union is enforced where it matters —
  // the Zod enum on save and the Settings card's checkbox list.
  const hiddenNavSections = useMemo<Set<string>>(
    () => new Set(organization?.settings?.hiddenNavSections ?? []),
    [organization?.settings?.hiddenNavSections],
  );
  const branding = useBranding();
  const { profile } = useAuth();
  const { isAdmin, isPlatformAdmin, isStationUser, isDtfPrinter, canViewReporting, canManageRules, can, role } = useRole();
  const { t } = useTranslation('nav');

  const isStation = isStationUser();
  const isViewer = role === 'Viewer';

  // Per-user visibility for Viewer role (BLU-384). Empty for everyone else;
  // filtering below short-circuits when isViewer is false so the existing
  // role-perm checks remain authoritative for other roles.
  const viewerPages = profile?.viewer_visible_pages ?? [];
  const viewerVisiblePagesSet = useMemo(() => new Set(viewerPages), [viewerPages]);
  const viewerAvailableWorkspaces = useMemo(
    () => (isViewer ? visibleWorkspaces(viewerPages) : (['inventory', 'production'] as const)),
    [isViewer, viewerPages],
  );

  // Auto-pin a Viewer to their only available workspace so they don't land on
  // an empty nav (e.g. admin selected only inventory pages but localStorage
  // remembers 'production' from a previous session).
  useEffect(() => {
    if (isViewer && viewerAvailableWorkspaces.length === 1) {
      const forced = viewerAvailableWorkspaces[0];
      if (forced && workspace !== forced) {
        setWorkspace(forced);
      }
    }
  }, [isViewer, viewerAvailableWorkspaces, workspace, setWorkspace]);

  // BLU-658: a pure 3PL client (owns no location — fulfils only via a
  // junction at another org's shared location) has no legitimate use for the
  // Production workspace; the client order screen is their only fulfilment
  // window. `isPureClient` stays false until the surfaces query resolves, so
  // this never flash-hides Production or auto-pins away from it before we
  // actually know the answer — mirrors the Viewer auto-pin above, forcing the
  // inventory workspace, but navigates straight to /orders (not the bare '/'
  // `setWorkspace('inventory')` would) so this agrees with
  // ProductionAccessGuard's own /production/* redirect target — AppSidebar
  // renders (and so runs its effects) before the routed Outlet content, so if
  // the two disagreed on a destination this one would win the race.
  // BLU-670: two signals now — `isPureClient` (junction + no self-run floor)
  // drives the client IA (nav sections, inbox, onboarding steps);
  // `hidesProduction` (no self-run floor, junction-agnostic) drives Production
  // visibility and exactly mirrors the backend requireOwnedLocation guard.
  const { isPureClient, hidesProduction, surfaces, isLoading: surfacesLoading } = useOrgRoleSurfaces();
  // BLU-711 A2: stocktake proposals only exist across a 3PL junction — a count
  // one org ran against ANOTHER org's rows. A single-tenant org can never have
  // one, so the nav entry would be a permanently empty page. Show it to the
  // two orgs that can: the provider whose warehouses hold client stock
  // (`render3PLSurface`, the same signal that renders the Locations page's
  // "Warehouses you fulfil for clients" section) and the client with a
  // junction of its own (`renderClientSurface`, the client-inbox signal).
  // Undefined while the query is loading or the shared-locations build flag is
  // off, and hidden in both cases — the route itself stays directly reachable.
  const showsStocktakes =
    surfaces != null && (surfaces.render3PLSurface || surfaces.renderClientSurface);
  // BLU-1295: the owner Client messages inbox exists only for a 3PL provider
  // that runs a floor for clients, and is read-gated on orders.view (same as
  // the route's RoleGuard, the procedure and the handler).
  const showsClientMessages = surfaces?.render3PLSurface === true && can('orders.view');
  // A pure client has no floor, so the Production WORKSPACE stays hidden — but
  // it may read the issues on its OWN orders at the 3PL's floor (PR #1514
  // relaxed the matching backend gate to `productionReadProcedure`). Exempt
  // that one path, or the client nav entry below would bounce straight back to
  // /orders. Everything else under /production still redirects.
  const isClientPermittedProductionPath = isPureClient && location.pathname === CLIENT_PRODUCTION_PATH;
  // BLU-367 CX access: same shape as isClientPermittedProductionPath above,
  // but for the whole CX nav (Tracker + Issues) rather than one legacy path —
  // CX is purpose-built to work for a pure-client org at a shared floor.
  const isCxRole = role === 'CX';
  useEffect(() => {
    if (hidesProduction && workspace === 'production' && !isClientPermittedProductionPath && !isCxRole) {
      navigate('/orders', { replace: true });
    }
  }, [hidesProduction, workspace, navigate, isClientPermittedProductionPath, isCxRole]);

  // Production-specific hooks (safe to call unconditionally)
  const batchNotification = useBatchNotificationSafe();
  // getStationSettings is not on the DTF Printer's procedure allowlist, so the
  // read stays off for it. dtfVisible then fails open (unresolved), which is
  // harmless: its three film items are not dtfVisible-gated.
  const { settings: stationSettings, isLoading: isLoadingSettings, isResolved: isSettingsResolved } = useStationSettings({
    enabled: !isDtfPrinter(),
  });
  // Whether the DTF/film workflow is visible in this org's nav — shared by
  // the coordinator's DTF section AND the station allowlist's film-line
  // entries (prod-film-print / prod-pressing / prod-film-storage), so an org
  // that turns DTF off loses those stations from BOTH nav surfaces, matching
  // the toggle's own copy ("hidden from the production nav").
  //
  // Fails OPEN on every state that is not a real answer. `dtfEnabled` defaults
  // to false, which is correct ONLY for a genuinely-new org that has answered;
  // it must never be read as "off" when the query never ran. Three states are
  // "we do not know", and all three must show DTF rather than hide it:
  //   - SKIPPED: useStationSettings passes skipToken until LocationContext has
  //     a location. react-query leaves that at pending/idle, so isLoading is
  //     false and isError is false — it looks answered and resolves to false.
  //   - LOADING: the query is in flight.
  //   - ERRORED: a Viewer hits getStationSettings, a stationProcedure they
  //     aren't permitted to call, so it throws FORBIDDEN. Hiding on that would
  //     drop the section for every Viewer in every org — a regression vs main,
  //     where these items were ungated.
  // isSuccess (isResolved) is the only state carrying a real answer, so gate on
  // it. Direction of the trade-off: a DTF-off org may briefly see the section
  // before it resolves away, which beats the ~90%-DTF orgs this section exists
  // for having it pop in late (or, in the skipped case, never appear at all).
  const dtfVisible = !isSettingsResolved || stationSettings.dtfEnabled;
  // Per-user hidden issue tabs (localStorage) — hidden tabs drop out of the
  // Production Issues nav badge so the total always equals the sum of the
  // tab badges the user can actually see.
  const { settings: productionSettings } = useProductionSettings();
  const { selectedLocation } = useLocationContext();

  // Skip coordinator-only queries for station users to avoid FORBIDDEN errors.
  // Pass skip values (empty/null/undefined) so the hooks' internal enabled checks disable them.
  const { data: issueCounts } = useIssueCounts(isStation ? '' : (selectedLocation?.id ?? ''));
  const { data: shippingData } = useShippingFailures(isStation ? null : (selectedLocation?.id ?? null));
  const portlessBlocked = usePortlessBlockedCount(isStation ? null : (selectedLocation?.id ?? null));
  const { data: dashboardStats } = useDashboard(isStation ? undefined : selectedLocation?.id);

  // Sum of the VISIBLE issue-tab badges (shared accessor with the Issues page
  // tab strip). Undefined counts while loading → 0, matching prior behavior.
  // The badge must always equal the sum of the tab badges the user can
  // actually open, so the role/org-shape tab filters that IssuesPage applies
  // (CX_HIDDEN_ISSUE_TABS / CLIENT_HIDDEN_ISSUE_TABS) are folded in here too.
  const totalIssues = computeVisibleIssueTotal(
    issueCounts,
    shippingData?.unresolvedCount ?? 0,
    [
      ...productionSettings.hiddenIssueTabs,
      ...(isCxRole ? CX_HIDDEN_ISSUE_TABS : []),
      ...(isPureClient ? CLIENT_HIDDEN_ISSUE_TABS : []),
    ],
    portlessBlocked,
  );

  const outstandingOrders = dashboardStats?.stages.totalOutstanding ?? 0;

  // BLU-665 — pure-client Orders badge: total unread order-chat messages.
  // orderIds: [] skips the last-message lookup and returns only the caller's
  // unread totals; 60s cadence matches the client inbox's background poll.
  // BLU-1295: also the owner's Client messages badge, polled only while that
  // item actually renders: the Production sections (not CX's unified sidebar,
  // not the station list), with a Viewer's own allowlist standing in for the
  // permission check exactly as isItemVisible does. The owner inbox page reads
  // this same cache entry for its row badges.
  const clientMessagesItemVisible =
    workspace === 'production' &&
    !isStation &&
    !isCxRole &&
    (isViewer ? viewerVisiblePagesSet.has('prod-client-messages') : showsClientMessages);
  const orderMessagesMeta = trpc.orderMessages.inboxMeta.useQuery(
    { orderIds: [] },
    {
      enabled: isPureClient || clientMessagesItemVisible,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      staleTime: 30_000,
    },
  );
  // A disabled, never-fetched query yields undefined, so this is 0 off-surface.
  const unreadOrderMessages = orderMessagesMeta.data?.unreadTotal ?? 0;

  const isInventory = !isStation && workspace === 'inventory';

  // Xero workspace nav gate — the /xero entry only appears once Xero is
  // connected. getStatus is a protectedProcedure callable by any member; skip
  // it for station users (no inventory nav) to avoid a needless request.
  const xeroStatusQuery = trpc.xero.getStatus.useQuery(undefined, {
    enabled: !isStation && workspace === 'inventory',
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const showXero = !isStation && (xeroStatusQuery.data?.connected ?? false);

  // "Get set up" (BLU-1376, Q3): only a platform admin on an org that still
  // needs setup (`orgNeedsSetup` in setup-mode.ts: `setup_required`, no
  // `setup_completed_at`, and not a pure fulfilment 3PL provider) sees the
  // entry; the finish latch hides it for good. The status query is not even
  // enabled for anyone else, so every other user makes no request.
  const showSetupEntry =
    !isStation && workspace === 'inventory' && shouldShowSetupEntry(organization, isPlatformAdmin());
  const onboardingStatusQuery = trpc.productSync.getOnboardingStatus.useQuery(undefined, {
    enabled: showSetupEntry,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  // The SAME derivation the onboarding page uses (server flags, never browser
  // storage), so the meter and the page can never disagree. No meter while
  // the flow is the print-mode question or still loading.
  const onboardingProgress = useMemo(
    () =>
      selectSetupProgress({
        status: onboardingStatusQuery.data,
        setupRequired: organization?.setup_required === true,
        setupCompletedAt: organization?.setup_completed_at ?? null,
        isPureClient,
        surfacesLoading,
      }),
    [onboardingStatusQuery.data, organization?.setup_required, organization?.setup_completed_at, isPureClient, surfacesLoading],
  );

  // BLU-666 — a pure 3PL client gets its OWN nav, not the owner nav with
  // conditions sprinkled through it: their job is "watch my orders, keep my
  // catalog synced, buy stock". Everything tied to running a warehouse
  // (Stock Alerts, Product Dev, SKU Rules, Cost of Goods, Consumables, Logs,
  // Reporting) is owner-only and simply absent here, so the list stays short
  // and every remaining item is one they can act on. Stock Sync is the
  // exception — client admins can trigger their own Shopify push, so it's
  // exposed (admin-gated) in the Catalog group below.
  //
  // Orders leads and is their landing page (see HomeRouteSwitch); Purchase
  // Orders lives at /purchase-orders because /orders is their order inbox
  // (OrdersRouteSwitch) — the owner nav keeps PO at /orders, unchanged.
  // Shared by clientNavSections and cxNavSections' pure-client branch — a
  // named const (not a hand-copied literal) so the two can't drift, e.g.
  // silently dropping the orders.view check.
  const clientOrdersNavItem: NavItem = {
    id: 'orders',
    path: '/orders',
    label: t('inventory.clientOrders'),
    icon: ShoppingCart,
    badge: unreadOrderMessages || undefined,
    check: () => can('orders.view'),
  };

  const clientNavSections: NavSection[] = [
    {
      id: 'client-daily',
      items: [
        clientOrdersNavItem,
        // Issues on the client's OWN orders at the 3PL's floor. Same page the
        // owner uses; the rows are already scoped to the client's org by
        // `resolveAccessibleOrganizations`, and the floor-only tabs are dropped
        // for clients (CLIENT_HIDDEN_ISSUE_TABS).
        {
          id: 'client-issues',
          path: CLIENT_PRODUCTION_PATH,
          label: t('production.issues'),
          icon: AlertTriangle,
          badge: totalIssues || undefined,
          check: () => can('orders.view'),
        },
        // BLU-770: pure clients have no Reports nav. This opens the 3PL usage
        // tab (their own bill at the 3PL's location), the only Reports view a
        // pure client gets: Reporting shows it for any ?tab= and hides the tab
        // bar (resolveReportingView). The path carries a query string, so the
        // id-keyed line in isActive keeps it highlighted on any /reporting URL.
        {
          id: 'reporting',
          path: '/reporting?tab=3pl-usage',
          label: t('inventory.threePlUsage'),
          icon: BarChart3,
          check: canViewReporting,
        },
      ],
    },
    {
      id: 'client-catalog',
      label: t('groupLabel.catalog', 'Catalog'),
      items: [
        // '/inventory' (not '/') — for a client '/' bounces to their order
        // inbox, so the stock dashboard needs its own reachable path.
        { id: 'inventory', path: '/inventory', label: t('inventory.dashboard'), icon: Package },
        // BLU-711 A2: counts the 3PL ran against this client's rows, waiting on
        // the client's approval. Both sides of the junction use this surface.
        { id: 'warehouse-positions', path: '/inventory/positions', label: t('inventory.positions'), icon: Boxes, check: () => can('inventory.view') },
        { id: 'stocktakes', path: '/inventory/stocktakes', label: t('inventory.stocktakes'), icon: ClipboardCheck, check: () => can('stocktakes.view') && showsStocktakes },
        { id: 'products', path: '/products', label: t('inventory.products'), icon: Tag },
        { id: 'product-sync', path: '/product-sync', label: t('inventory.productSync'), icon: RefreshCw, check: () => can('org_settings.read') },
        { id: 'stock-sync', path: '/stock-sync', label: t('inventory.stockSync'), icon: ArrowDownUp, check: () => isAdmin() },
      ],
    },
    {
      id: 'client-ordering',
      label: t('groupLabel.ordering', 'Ordering'),
      items: [
        { id: 'purchase-orders', path: '/purchase-orders', label: t('inventory.orders'), icon: ClipboardList, check: () => can('purchase_orders.view') },
        { id: 'transfers', path: '/transfers', label: t('inventory.transfers'), icon: ArrowLeftRight, check: () => can('purchase_orders.view') },
        { id: 'shipments', path: '/shipments', label: t('inventory.shipments'), icon: Ship, check: () => can('purchase_orders.view') },
        { id: 'suppliers', path: '/suppliers', label: t('inventory.suppliers'), icon: Truck, check: () => can('suppliers.view') || can('suppliers.manage') },
      ],
    },
  ];

  // Inventory nav is grouped into labelled sections (IA regroup). Daily drivers
  // sit ungrouped at the top; the rest cluster by workflow. Permission-based
  // checks match the canonical surfaces so view-only roles (Viewer) see the same
  // items as .manage roles — write actions stay gated per-page.
  const ownerNavSections: NavSection[] = [
    {
      id: 'daily',
      items: [
        { id: 'inventory', path: '/', label: t('inventory.dashboard'), icon: Package },
        { id: 'products', path: '/products', label: t('inventory.products'), icon: Tag },
        { id: 'stock-alerts', path: '/stock-alerts', label: t('inventory.stockAlerts'), icon: AlertTriangle, check: () => can('stock_alerts.view') },
      ],
    },
    {
      id: 'ordering',
      label: t('groupLabel.ordering', 'Ordering'),
      items: [
        // Daily ordering flow first (POs, transfers, shipments, suppliers);
        // Product Dev is lower-frequency so it sits at the bottom of the group.
        // Owner/hybrid orgs only — a pure client renders clientNavSections
        // instead, where /orders is their order inbox and PO moves to
        // /purchase-orders.
        { id: 'orders', path: '/orders', label: t('inventory.orders'), icon: ShoppingCart, check: () => can('purchase_orders.view') },
        { id: 'transfers', path: '/transfers', label: t('inventory.transfers'), icon: ArrowLeftRight, check: () => can('purchase_orders.view') },
        { id: 'shipments', path: '/shipments', label: t('inventory.shipments'), icon: Ship, check: () => can('purchase_orders.view') },
        { id: 'suppliers', path: '/suppliers', label: t('inventory.suppliers'), icon: Truck, check: () => can('suppliers.view') || can('suppliers.manage') },
        { id: 'product-dev', path: '/product-dev', label: 'Product Dev', icon: Layers, check: () => can('product_dev.view') },
      ],
    },
    {
      id: 'stock-ops',
      label: t('groupLabel.stockOps', 'Stock & Ops'),
      // Default-collapsed accordion — these are lower-frequency admin/ops
      // surfaces; keeping them tucked away shortens the nav so it fits the
      // viewport without an interior scroll rail.
      collapsible: 'closed',
      items: [
        // BLU-711 A2: the owner side of the same surface — counts this org ran
        // for its clients, and their approval state. A hybrid org (runs its own
        // floor AND is someone else's client) renders THIS nav, so the client
        // half of the gate has to live here too.
        { id: 'warehouse-positions', path: '/inventory/positions', label: t('inventory.positions'), icon: Boxes, check: () => can('inventory.view') },
        { id: 'stocktakes', path: '/inventory/stocktakes', label: t('inventory.stocktakes'), icon: ClipboardCheck, check: () => can('stocktakes.view') && showsStocktakes },
        { id: 'product-sync', path: '/product-sync', label: t('inventory.productSync'), icon: RefreshCw, check: () => can('org_settings.read') },
        { id: 'rules', path: '/rules', label: t('inventory.rules'), icon: Braces, check: () => canManageRules() },
        { id: 'cost-of-goods', path: '/cost-of-goods', label: t('inventory.costOfGoods'), icon: DollarSign, check: () => can('skus.costs_manage') },
        { id: 'stock-hold-back', path: '/stock-hold-back', label: t('inventory.holdBack'), icon: ShieldMinus, check: () => isAdmin() },
        { id: 'consumables', path: '/consumables', label: t('inventory.consumables'), icon: Beaker, check: () => can('consumables.view') || can('consumables.manage') },
        { id: 'stock-sync', path: '/stock-sync', label: t('inventory.stockSync'), icon: ArrowDownUp, check: () => isAdmin() },
      ],
    },
    {
      id: 'insights',
      label: t('groupLabel.insights', 'Insights'),
      items: [
        { id: 'logs', path: '/logs', label: t('inventory.logs'), icon: FileText, check: () => can('logs.view') },
        { id: 'reporting', path: '/reporting', label: t('inventory.reporting'), icon: BarChart3, check: canViewReporting },
      ],
    },
  ];

  // Pinned footer — admin/system chrome. SKU Rules + Cost of Goods moved up into
  // the Catalog section; Xero (main list previously) moved down here, shown only
  // when connected.
  const inventoryFooterItems: NavItem[] = [
    // Get-set-up sits at the top of the pinned footer (above Notifications) as a
    // setup companion for platform admins on an org still in setup (gate in
    // `showSetupEntry`, 3PL-provider exclusion in `orgNeedsSetup`). Its meter comes from
    // `selectSetupProgress` and is absent on the print-mode question.
    { id: 'get-set-up', path: '/onboarding', label: t('inventory.getSetUp'), icon: Rocket, check: () => showSetupEntry, progress: onboardingProgress.showMeter ? { done: onboardingProgress.doneCount, total: onboardingProgress.total } : undefined },
    { id: 'notifications', path: '/notifications', label: 'Notifications', icon: Bell, check: () => isAdmin() },
    { id: 'settings', path: '/settings', label: t('inventory.settings'), icon: SlidersHorizontal },
    // Help Center — global bilingual video guides; visible to every inventory role
    // (Viewer visibility comes from the page-registry 'help' entry).
    { id: 'help', path: '/help', label: t('footer.help'), icon: BookOpen },
    ...(showXero
      ? [{ id: 'xero', path: '/xero', label: 'Xero', icon: Plug, check: () => can('org_settings.read') } as NavItem]
      : []),
  ];

  // Production nav is grouped into labelled sections (IA regroup, BLU-653),
  // mirroring the inventory regroup above. Dashboard sits ungrouped at the
  // top; the daily order-tracking flow, the physical stations, and the DTF
  // film line each cluster into their own workflow group.
  const productionNavSections: NavSection[] = [
    {
      id: 'prod-daily',
      items: [
        { id: 'prod-dashboard', path: '/production/dashboard', label: t('production.dashboard'), icon: LayoutGrid },
      ],
    },
    {
      id: 'prod-order-mgmt',
      label: t('groupLabel.orderManagement', 'Order Management'),
      items: [
        { id: 'prod-orders', path: '/production/orders', label: t('production.orders'), icon: ClipboardList, badge: outstandingOrders || undefined },
        { id: 'prod-client-messages', path: '/production/client-messages', label: t('production.clientMessages'), icon: MessagesSquare, badge: unreadOrderMessages || undefined, check: () => showsClientMessages },
        { id: 'prod-preprint', path: '/production/pre-print', label: t('production.preprint', 'Pre-Print'), icon: Layers },
        { id: 'prod-issues', path: '/production/issues', label: t('production.issues'), icon: AlertTriangle, badge: totalIssues || undefined },
        { id: 'prod-tracker', path: '/production/tracker', label: t('production.tracker'), icon: Activity },
      ],
    },
    {
      id: 'prod-stations',
      label: t('groupLabel.stations', 'Stations'),
      collapsible: 'closed',
      items: [
        { id: 'prod-picker', path: '/production/picker', label: t('production.picker'), icon: Package2, indicator: batchNotification?.showPickerIndicator },
        ...(!isLoadingSettings && stationSettings.pretreatStationEnabled ? [
          { id: 'prod-pretreat', path: '/production/pretreat', label: t('production.pretreat'), icon: Droplet } as NavItem,
        ] : []),
        { id: 'prod-print', path: '/production/printer', label: t('production.printer'), icon: Printer },
        { id: 'prod-catcher', path: '/production/catcher', label: t('production.catcher'), icon: Scan },
        { id: 'prod-packing', path: '/production/packer', label: t('production.packer'), icon: PackageOpen },
        { id: 'prod-bins', path: '/production/bins', label: t('production.bins'), icon: Archive },
      ],
    },
    // DTF film line — a whole extra workflow most orgs don't run. The section
    // drops out entirely (not just the label) when the org has confirmed the
    // DTF station toggle off; see `dtfVisible` above for why every unresolved
    // state keeps it visible instead. No `collapsible` here (unlike Stations):
    // the dtfEnabled gate is already the filter for the ~90%-DTF orgs this
    // section exists for — collapsing it too would double-penalise them.
    ...(dtfVisible ? [
      {
        id: 'prod-dtf',
        label: t('groupLabel.dtf', 'DTF'),
        items: [
          { id: 'prod-film-print', path: '/production/film-printing', label: t('production.filmPrinting'), icon: Film },
          // Phase 50.02 pulled forward / BLU-233: PRE-PRINTED DTF film stock (coordinator-only).
          { id: 'prod-film-preprint', path: '/production/film-preprint', label: t('production.filmPreprint'), icon: Boxes },
          // Matching station (Concept B): pull film from its hole + scan-confirm the pairing.
          { id: 'prod-matching', path: '/production/matching', label: t('production.matching'), icon: Layers },
          { id: 'prod-pressing', path: '/production/pressing', label: t('production.pressing'), icon: Flame },
          { id: 'prod-film-storage', path: '/production/film-storage', label: t('production.filmStorage'), icon: FolderArchive },
        ],
      } as NavSection,
    ] : []),
    {
      id: 'prod-insights',
      label: t('groupLabel.insights'),
      items: [
        { id: 'prod-reports', path: '/production/reports', label: t('production.reports'), icon: BarChart3 },
      ],
    },
  ];

  // Station users get a focused allowlist: their station + tracker (+ bins for
  // Packer). See buildStationNavItems for the dtfVisible gating and the DTF
  // Printer's three film pages.
  const stationNavItems: NavItem[] = buildStationNavItems({
    role,
    dtfVisible,
    can,
    t: (key) => t(key),
    showPickerIndicator: batchNotification?.showPickerIndicator,
  });

  // BLU-367 CX access: a single unified sidebar (not the usual Inventory/
  // Production workspace TOGGLE — CX only ever has 3 destinations, so a
  // toggle just adds a click) with two labelled sections. Production holds
  // Tracker (with its Order Queue tab) + Issues; Inventory holds the same
  // stock dashboard owner/hybrid orgs land on at '/'. No batch/station/
  // settings items. The Order Queue page itself is deliberately absent —
  // it's folded into Tracker as a tab, so CX never needs that route.
  const cxNavSections: NavSection[] = [
    {
      id: 'cx-production',
      label: t('groupLabel.production'),
      collapsible: 'open',
      items: [
        { id: 'prod-tracker', path: '/production/tracker', label: t('production.tracker'), icon: Activity },
        { id: 'prod-issues', path: '/production/issues', label: t('production.issues'), icon: AlertTriangle, badge: totalIssues || undefined },
      ],
    },
    {
      // The second CX section depends on org shape. A hybrid org (e.g.
      // ThreadHeads — runs its own floors, outsources Ningbo) gets the stock
      // dashboard at '/'. A pure-client org would have '/' bounce to /orders
      // (HomeRouteSwitch), and its CX work IS the orders inbox — so it gets
      // the client Orders item instead of a dead Inventory link.
      // Collapsible only when labelled — the accordion trigger IS the label,
      // so the pure-client (label-less) shape stays a plain fixed section.
      id: 'cx-inventory',
      label: isPureClient ? undefined : t('groupLabel.inventory'),
      collapsible: isPureClient ? undefined : 'open',
      items: [
        isPureClient
          ? clientOrdersNavItem
          : { id: 'inventory', path: '/', label: t('inventory.dashboard'), icon: Package },
      ],
    },
  ];

  // Pinned footer for the production/station workspace.
  const productionFooterItems: NavItem[] = [
    // MZ bridge control panel (Phase 2/3) — coordinator+ (production_settings.manage).
    // Hidden for station users (mirrors Production Settings below).
    ...(!isStation
      ? [{ id: 'prod-bridge', path: '/production/bridge', label: t('production.bridge', 'Bridge'), icon: Cable, check: () => can('production_settings.manage') } as NavItem]
      : []),
    // Production Settings — hidden for station users, and permission-gated to
    // match its own route guard (/production/settings is Super Admin / Admin /
    // Warehouse Coordinator — exactly the holders of production_settings.manage,
    // the same check the Bridge item above uses). Without the check the link
    // rendered for every non-station role that can now reach the Production
    // workspace (Warehouse Staff, CX) and bounced them straight back out.
    ...(!isStation
      ? [{ id: 'prod-settings', path: '/production/settings', label: t('production.settings'), icon: Settings, check: () => can('production_settings.manage') } as NavItem]
      : []),
    { id: 'prod-help', path: '/help', label: t('production.help'), icon: BookOpen },
  ];

  // Partner portal nav gate — only orgs with ≥1 active partnership (and the
  // feature flag on) get the section. getStatus is callable by any member and
  // returns { enabled:false, partnerships: [] } rather than erroring.
  const partnerStatusQuery = trpc.supplierPortal.getStatus.useQuery(undefined, {
    enabled: !isStation,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const showPartnerPortal =
    !isStation &&
    workspace === 'inventory' &&
    (partnerStatusQuery.data?.enabled ?? false) &&
    (partnerStatusQuery.data?.partnerships.length ?? 0) > 0;

  const partnerNavItems: NavItem[] = [
    { id: 'partner-orders', path: '/partner/orders', label: 'Incoming Orders', icon: Inbox },
    { id: 'partner-product-dev', path: '/partner/product-dev', label: 'Product Dev', icon: Layers },
  ];

  // For Viewer, the per-user `viewer_visible_pages` list is authoritative —
  // skip the role-perm `check` and filter strictly by visibility. For everyone
  // else, the existing role-perm check applies.
  const isItemVisible = (item: NavItem): boolean => {
    if (isViewer) return viewerVisiblePagesSet.has(item.id);
    return !item.check || item.check();
  };

  // Org-level decluttering: a storeless 3PL hides sections it structurally
  // never uses (Ordering, Stock & Ops). COSMETIC ONLY — the routes stay
  // reachable and the endpoints still serve; this removes chrome, not access.
  // Anything that must actually be denied belongs in RBAC.
  const isSectionVisible = (section: NavSection): boolean =>
    !hiddenNavSections.has(section.id);

  const isActive = (item: NavItem) => {
    if (item.path === '/' && location.pathname === '/') return true;
    if (item.path !== '/') {
      if (location.pathname.startsWith(item.path)) return true;
      // Batch detail pages are part of the Picker workflow
      if (item.path === '/production/picker' && location.pathname.startsWith('/production/batches')) return true;
      // The client "3PL usage" item links to /reporting?tab=3pl-usage
      if (item.id === 'reporting' && location.pathname.startsWith('/reporting')) return true;
    }
    return false;
  };

  return (
    <Sidebar collapsible="icon" className={reveal ? 'border-r-0 motion-safe:animate-fade-in' : 'border-r-0'}>
      <SidebarHeader className="p-4 group-data-[collapsible=icon]:p-2">
        {/* Logo — hidden when collapsed */}
        <div className="flex items-center gap-3 group-data-[collapsible=icon]:hidden">
          <img
            src={branding.logoUrl}
            alt={branding.displayName}
            className="h-8 cursor-pointer shrink-0"
            onClick={() => navigate('/')}
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              const fallback = '/brand/printrun-logo-white.png';
              if (img.src.endsWith(fallback)) return;
              const uploadedLogoUrl = organization?.settings?.branding?.logoUrl;
              const legacySlugUrl = organization?.slug ? `/org-logos/${organization.slug}.png` : null;
              // Only a failed CUSTOM upload gets a second try at the legacy slug PNG —
              // resolveBranding() already returns the slug URL directly when there's no
              // upload, so that failure goes straight to the ThreadHeads fallback.
              if (uploadedLogoUrl && branding.logoUrl === uploadedLogoUrl && legacySlugUrl && !img.src.endsWith(legacySlugUrl)) {
                img.src = legacySlugUrl;
                return;
              }
              img.src = fallback;
            }}
          />
        </div>

        {/* Workspace Toggle — hidden for station users, for CX (single unified
            sidebar, no workspace concept), for Viewers whose visible-pages
            allowlist only spans a single workspace, and (BLU-658/670) for any
            org with no self-run floor (Production would 403 server-side —
            hidesProduction mirrors that guard). */}
        {!isStation && !isCxRole && !(isViewer && viewerAvailableWorkspaces.length <= 1) && !hidesProduction && (
          <div className="mt-3 group-data-[collapsible=icon]:hidden">
            <div className="flex rounded-lg bg-sidebar-accent p-1 gap-1">
              <button
                onClick={() => setWorkspace('inventory')}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-[background-color,color]',
                  workspace === 'inventory'
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                )}
              >
                {t('workspace.inventory')}
              </button>
              <button
                onClick={() => setWorkspace('production')}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-[background-color,color]',
                  workspace === 'production'
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                )}
              >
                {t('workspace.production')}
              </button>
            </div>
          </div>
        )}

        {/* Station/CX role indicator — replaces the workspace toggle for
            users who have no workspace concept (station roles, and CX's
            single unified sidebar). */}
        {(isStation || isCxRole) && role && (
          <div className="mt-3 group-data-[collapsible=icon]:hidden">
            <div className="rounded-lg bg-sidebar-accent px-3 py-2 text-xs font-medium text-sidebar-foreground/70">
              {STATION_LABELS[role] ?? role}
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {isCxRole ? (
          // BLU-367 CX access — a single unified sidebar, not the usual
          // Inventory/Production workspace toggle: CX only has 3 destinations
          // total, so checked FIRST and independent of `workspace` state (no
          // toggle click needed, no dependency on which page they land on
          // first). Same labelled-section shape as ownerNavSections below.
          cxNavSections.map((section) => {
            const visibleItems = section.items.filter(isItemVisible);
            if (visibleItems.length === 0) return null;
            return (
              <NavSectionGroup
                key={section.id}
                section={section}
                visibleItems={visibleItems}
                isActive={isActive}
              />
            );
          })
        ) : isInventory ? (
          // Inventory workspace — labelled workflow sections. Sections with no
          // visible items (permission / Viewer allowlist) drop out entirely so
          // no orphan label ever renders.
          (isPureClient ? clientNavSections : ownerNavSections)
            .filter(isSectionVisible)
            .map((section) => {
            const visibleItems = section.items.filter(isItemVisible);
            if (visibleItems.length === 0) return null;
            return (
              <NavSectionGroup
                key={section.id}
                section={section}
                visibleItems={visibleItems}
                isActive={isActive}
              />
            );
          })
        ) : isStation ? (
          // Station workspace — focused allowlist, single flat group, no label
          // (only 2-3 items; a regrouped accordion would be noise here).
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {stationNavItems.filter(isItemVisible).map((item) => (
                  <SidebarNavItem key={item.id} item={item} active={isActive(item)} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          // Production workspace — labelled workflow sections (BLU-653 regroup),
          // same pattern as inventory above.
          productionNavSections.map((section) => {
            const visibleItems = section.items.filter(isItemVisible);
            if (visibleItems.length === 0) return null;
            return (
              <NavSectionGroup
                key={section.id}
                section={section}
                visibleItems={visibleItems}
                isActive={isActive}
              />
            );
          })
        )}

        {/* Partner Portal — supplier-org view of a buyer's shared POs +
            product dev. Only rendered for orgs with an active partnership. */}
        {showPartnerPortal && (
          <SidebarGroup>
            <SidebarGroupLabel className={GROUP_LABEL_CLASS}>
              Partner Portal
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {partnerNavItems.filter(isItemVisible).map((item) => (
                  <SidebarNavItem key={item.id} item={item} active={isActive(item)} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          {/* CX has ONE unified sidebar with no workspace toggle, so it gets one
              footer too — pinned to the inventory set regardless of which route
              it is on. Keying the footer off `workspace` (derived from the
              pathname) meant a CX user on /production/tracker got the PRODUCTION
              footer, whose Settings link is coordinator-only and bounced them
              back to Tracker. Its permission set already resolves to exactly
              Settings + Help for CX. */}
          {(isInventory || isCxRole) &&
            inventoryFooterItems
              .filter(isItemVisible)
              .map((item) => <SidebarNavItem key={item.id} item={item} active={isActive(item)} />)}
          {(workspace === 'production' || isStation) && !isCxRole &&
            productionFooterItems
              .filter(isItemVisible)
              .map((item) => <SidebarNavItem key={item.id} item={item} active={isActive(item)} />)}
        </SidebarMenu>
        {/* Outside SidebarMenu on purpose: this is not a nav item and must not
            pick up menu-item hover/active chrome. This one footer serves both
            the inventory and production sidebar item sets, so a single insertion
            covers every surface that has the sidebar. */}
        <BuildIdLabel
          variant="compact"
          className="px-2 pt-1 text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden"
        />
      </SidebarFooter>
    </Sidebar>
  );
}

/**
 * Safe wrapper for useBatchNotification that returns null when outside BatchNotificationProvider.
 * The sidebar renders for all workspaces but BatchNotificationProvider only wraps production routes.
 */
function useBatchNotificationSafe() {
  try {
    return useBatchNotification();
  } catch {
    return null;
  }
}
